/* femme Near Expiry storefront bridge V1 | 2026-11-03
   The existing Near Expiry Discount Function remains authoritative for the
   actual cart/checkout discount. This bridge only controls storefront purchase
   mode and presentation while the selected variant has active near-expiry stock.
*/
(() => {
  'use strict';

  if (window.__femmeNearExpiryStorefrontInstalled) return;
  window.__femmeNearExpiryStorefrontInstalled = true;

  const source = document.querySelector('[data-femme-near-expiry-data]');
  if (!source) return;

  let payload = {};
  try {
    payload = JSON.parse(source.textContent || '{}');
  } catch (error) {
    console.error('[femme Near Expiry] Invalid storefront data.', error);
    return;
  }

  const variants = new Map(
    (Array.isArray(payload?.variants) ? payload.variants : []).map((entry) => [
      String(entry?.id || ''),
      {
        quantity: Number(entry?.quantity || 0),
        discountPercentage: Number(entry?.discountPercentage || 0),
        expiryDate: String(entry?.expiryDate || '')
      }
    ])
  );

  const infoFor = (variant) => {
    const basePrice = Number(variant?.price || 0);
    const entry = variants.get(String(variant?.id || ''));
    const quantity = Number(entry?.quantity || 0);
    const discountPercentage = Number(entry?.discountPercentage || 0);
    const active =
      quantity > 0 &&
      discountPercentage > 0 &&
      discountPercentage <= 100 &&
      basePrice >= 0;

    return {
      active,
      quantity,
      discountPercentage,
      expiryDate: String(entry?.expiryDate || ''),
      basePrice,
      displayPrice: active
        ? Math.round(basePrice * (100 - discountPercentage) / 100)
        : basePrice
    };
  };

  const installStyles = () => {
    if (document.getElementById('femme-near-expiry-storefront-style')) return;

    const style = document.createElement('style');
    style.id = 'femme-near-expiry-storefront-style';
    style.textContent = `
      product-info.femme-near-expiry-active .fsc-ui[data-femme-appstle-ui="true"] {
        display: none !important;
      }
    `;
    document.head.appendChild(style);
  };

  installStyles();

  let attempts = 0;
  const installAppstleGuard = () => {
    const A = window.FemmeAppstleMigration;
    const S = A?.state;

    if (!A || !S || typeof A.render !== 'function' || typeof A.publish !== 'function') {
      attempts += 1;
      if (attempts < 100) window.setTimeout(installAppstleGuard, 25);
      return;
    }

    if (A.__femmeNearExpiryGuardInstalled) return;
    A.__femmeNearExpiryGuardInstalled = true;

    const nativeRender = A.render;
    const nativePublish = A.publish;
    const nativeUpdateHeadlinePrice = A.updateHeadlinePrice;

    const enterNearExpiry = () => {
      if (!S.nearExpiryLockActive) {
        S.nearExpiryPreviousSelection = {
          selectionType: S.selectionType,
          selectedPlanId: S.selectedPlanId
        };
      }

      S.nearExpiryLockActive = true;
      S.selectionType = 'one_time';
      S.selectedPlanId = '';
      S.root?.classList.add('femme-near-expiry-active');
    };

    const leaveNearExpiry = (variant) => {
      if (!S.nearExpiryLockActive) {
        S.root?.classList.remove('femme-near-expiry-active');
        return;
      }

      const previous = S.nearExpiryPreviousSelection || {};
      const plans = A.getPlans?.(variant) || [];

      if (previous.selectionType === 'one_time' || !plans.length) {
        S.selectionType = 'one_time';
        S.selectedPlanId = '';
      } else {
        S.selectionType = 'subscription';
        const previousPlanId = String(previous.selectedPlanId || '');
        const previousPlanIsValid = plans.some(
          (plan) => String(plan?.id || '') === previousPlanId
        );
        const fallbackPlan = A.defaultPlan?.(plans);
        S.selectedPlanId = previousPlanIsValid
          ? previousPlanId
          : String(fallbackPlan?.id || '');
      }

      S.nearExpiryLockActive = false;
      S.nearExpiryPreviousSelection = null;
      S.root?.classList.remove('femme-near-expiry-active');
    };

    const applyNearExpiryHeadline = (variant, info) => {
      if (!S.root || !variant || !info.active) return;

      const priceContainer = A.headlinePriceContainer?.();
      const priceRoot = priceContainer?.querySelector('.f-price');
      const discounted = A.money(info.displayPrice);
      const original = A.money(info.basePrice);

      if (priceRoot) {
        if (priceRoot.dataset.femmeNearExpiryPriceState !== 'true') {
          priceRoot.dataset.femmeNearExpiryHadSale =
            priceRoot.dataset.femmeNearExpiry === 'true'
              ? 'false'
              : priceRoot.classList.contains('f-price--on-sale')
                ? 'true'
                : 'false';
        }
        priceRoot.dataset.femmeNearExpiryPriceState = 'true';
        priceRoot.classList.add('f-price--on-sale');
      }

      priceContainer
        ?.querySelectorAll('[data-femme-headline-price]')
        .forEach((node) => {
          if (String(node.textContent || '').trim() !== discounted) {
            node.textContent = discounted;
          }
        });

      const compareNode = priceContainer?.querySelector('.f-price__sale s');
      if (compareNode && String(compareNode.textContent || '').trim() !== original) {
        compareNode.textContent = original;
      }

      S.root.classList.remove('femme-price-booting');
    };

    const clearNearExpiryHeadlineState = () => {
      const priceContainer = A.headlinePriceContainer?.();
      const priceRoot = priceContainer?.querySelector('.f-price');
      if (!priceRoot) return;

      const owned =
        priceRoot.dataset.femmeNearExpiryPriceState === 'true' ||
        priceRoot.dataset.femmeNearExpiry === 'true';
      if (!owned) return;

      if (priceRoot.dataset.femmeNearExpiryHadSale !== 'true') {
        priceRoot.classList.remove('f-price--on-sale');
      }

      delete priceRoot.dataset.femmeNearExpiryPriceState;
      delete priceRoot.dataset.femmeNearExpiryHadSale;
      delete priceRoot.dataset.femmeNearExpiry;
      delete priceRoot.dataset.femmeNearExpiryDiscount;
      if (priceRoot.dataset.femmeInitialPrice === 'near-expiry') {
        delete priceRoot.dataset.femmeInitialPrice;
      }
    };

    A.updateHeadlinePrice = (plan, variant) => {
      const info = infoFor(variant);
      if (!info.active) {
        clearNearExpiryHeadlineState();
        nativeUpdateHeadlinePrice?.(plan, variant);
        return;
      }

      enterNearExpiry();
      applyNearExpiryHeadline(variant, info);
    };

    A.publish = (plan, variant) => {
      const info = infoFor(variant);
      if (!info.active) {
        nativePublish(plan, variant);
        return;
      }

      enterNearExpiry();
      const root = S.root;
      if (!root || !variant) return;

      root.dataset.fscProvider = 'appstle';
      root.dataset.fscSelection = 'one_time';
      root.dataset.fscSelectedPlanId = '';
      root.dataset.fscSubscriptionPriceCents = '';
      root.dataset.fscOneTimePriceCents = String(info.displayPrice);
      root.dataset.femmeNearExpiry = 'true';
      root.dataset.femmeNearExpiryQuantity = String(info.quantity);
      root.dataset.femmeNearExpiryDiscount = String(info.discountPercentage);

      root.dispatchEvent(
        new CustomEvent('fsc:purchase-option-changed', {
          bubbles: true,
          detail: {
            productId: String(S.product?.id || ''),
            provider: 'appstle',
            selectionType: 'one_time',
            sellingPlanId: '',
            sellingPlanName: '',
            deliveryFrequency: '',
            subscriptionPriceCents: 0,
            oneTimePriceCents: info.displayPrice,
            nearExpiry: true,
            nearExpiryQuantity: info.quantity,
            nearExpiryDiscountPercentage: info.discountPercentage
          }
        })
      );
    };

    A.render = () => {
      const variant = A.getVariant?.();
      const info = infoFor(variant);

      if (info.active) {
        enterNearExpiry();
      } else {
        leaveNearExpiry(variant);
        clearNearExpiryHeadlineState();
        if (S.root) {
          delete S.root.dataset.femmeNearExpiry;
          delete S.root.dataset.femmeNearExpiryQuantity;
          delete S.root.dataset.femmeNearExpiryDiscount;
        }
      }

      nativeRender();

      if (!info.active) return;

      if (S.ui) S.ui.hidden = true;
      A.syncForms?.();
      applyNearExpiryHeadline(variant, info);
      S.root?.classList.remove('femme-price-booting');
    };
  };

  installAppstleGuard();
})();
